import { Options } from 'vue-class-component';
import SceneWizardStep from '../SceneWizardStep';
import ScenePanelObjects from '../ScenePanelObjects/ScenePanelObjects.vue';
import ScenePanelAggregateCommands from '../ScenePanelAggregateCommands/ScenePanelAggregateCommands.vue';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { HANDLE_REFRESH_PLANT } from '@/core/utils/emitter-events';
import plantsClient from '@/core/services/plants';
import {
  Action,
  ActionDTO,
  AggregateCommandActionListItemDTO,
  DefaultObjectAction,
  GeneralCommandsAndGroupsAction,
  IAction,
  IDefaultObjectAction,
  IGeneralCommandsAndGroupsAction,
  IObjectAction,
  IWaitTimeAction,
  ObjectAction,
  RoomObjectActionListItemDTO,
  SceneActionListItemDTO,
  SceneObjectType,
  WaitTimeAction,
} from '@/../client-generator/generated-clients/plants';
import ScenePanelTime from '../ScenePanelTime/ScenePanelTime.vue';

import SceneStepItemsList from '../SceneStepItemsList/SceneStepItemsList.vue';
import DialogSceneStepItemsListReorder from '../DialogSceneStepItemsListReorder/DialogSceneStepItemsListReorder.vue';
import DialogAddAction from '../DialogAddAction/DialogAddAction.vue';
import { isDefaultScene } from '@/core/utils/utils';

@Options({
  name: 'SceneWizardStep2',
  components: {
    ScenePanelObjects,
    ScenePanelAggregateCommands,
    ScenePanelTime,
    SceneStepItemsList,
    DialogAddAction,
    DialogSceneStepItemsListReorder,
  },
})
export default class SceneWizardStep2 extends SceneWizardStep {
  aggregateCommandsActions: AggregateCommandActionListItemDTO[] = [];
  roomObjectsActions: RoomObjectActionListItemDTO[] = [];
  isVisibleDialogEditAction = false;
  isVisibleDialogReorderAction = false;
  tempActionDTOToEdit: ActionDTO | null = null;
  tempActionToEdit: SceneActionListItemDTO | null = null;

  mounted() {
    this.fetchActionsList();
  }

  get actionText() {
    if (this.scene?.actions?.length == 1){
      return this.$t('AppResource.Action')
    }else{
      return this.$t('AppResource.Actions')
    }
  }

  async fetchActionsList() {
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      const response = await plantsClient.sceneActionsList(
        this.plantId as number
      );

      this.aggregateCommandsActions = response.aggregateCommandsActions as AggregateCommandActionListItemDTO[];
      this.roomObjectsActions = response.roomObjectsActions as RoomObjectActionListItemDTO[];
    } catch (error) {
      let messageError = '';
      switch (error.status) {
        case 409:
          this.$emitter.emit(HANDLE_REFRESH_PLANT);
          this.$router.push({
            name: 'page-plant-dashboard',
            params: this.$route.params,
          });
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      if (messageError) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('general.operationError'),
          closable: true,
          life: 3000,
        });
      }
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  handleAddAction(action: SceneActionListItemDTO) {
    //TODO: align with xamarin -> guid converted in int?!? -> max + 1?
    const idsArray = (this.scene.actions?.flatMap((x) => x.actionParams?.id) ??
      []) as number[];
    const uniqueId = idsArray.length ? Math.max(...idsArray) + 1 : 1;

    const theObjectAction: IObjectAction = {
      id: uniqueId,
      linkableObjectId: action.linkableObjectId,
    };
    const theDefaultObjectAction: IDefaultObjectAction = {
      id: uniqueId,
      linkableObjectId: action.linkableObjectId,
    };
    const theWaitingTimeAction: IWaitTimeAction = {
      id: uniqueId,
    };
    const theGroupsCommandsAction: IGeneralCommandsAndGroupsAction = {
      id: uniqueId,
      linkableObjectId: action.linkableObjectId,
    };

    let theAction: IAction | null = null;
    if (action.sceneObjectType == SceneObjectType.DateTime) {
      theAction = theWaitingTimeAction;
    } else if (
      [
        SceneObjectType.GeneralCommandsLights,
        SceneObjectType.GeneralCommandsShutters,
        SceneObjectType.GroupOfLights,
        SceneObjectType.GroupOfShutters,
      ].includes(action.sceneObjectType!)
    ) {
      theAction = theGroupsCommandsAction;
    } else if (isDefaultScene(this.scene)) {
      theAction = theDefaultObjectAction;
    } else {
      theAction = theObjectAction;
    }

    this.assignActionFields(action, theAction);

    let actionParams: Action | null = null;
    if (action.sceneObjectType == SceneObjectType.DateTime) {
      actionParams = new WaitTimeAction(theWaitingTimeAction);
    } else if (
      [
        SceneObjectType.GeneralCommandsLights,
        SceneObjectType.GeneralCommandsShutters,
        SceneObjectType.GroupOfLights,
        SceneObjectType.GroupOfShutters,
      ].includes(action.sceneObjectType!)
    ) {
      actionParams = new GeneralCommandsAndGroupsAction(
        theGroupsCommandsAction
      );
    } else if (isDefaultScene(this.scene)) {
      actionParams = new DefaultObjectAction(theDefaultObjectAction);
    } else {
      actionParams = new ObjectAction(theObjectAction);
    }
    this.scene.actions?.push(
      new ActionDTO({
        actionParams,
        objectName: action.name,
        sceneObjectType: action.sceneObjectType,
      })
    );
  }

  handleEdit(action: ActionDTO) {
    this.tempActionDTOToEdit = action;
    this.tempActionToEdit = new SceneActionListItemDTO({
      ...action.actionParams,
      sceneObjectType: action.sceneObjectType,
      name: action.objectName,
    });
    this.isVisibleDialogEditAction = true;
  }

  handleDoEditAction() {
    if (!this.tempActionToEdit || !this.tempActionDTOToEdit?.actionParams)
      return;
    this.assignActionFields(
      this.tempActionToEdit,
      this.tempActionDTOToEdit?.actionParams
    );
    this.tempActionToEdit;
  }

  assignActionFields(fromAction: SceneActionListItemDTO, toAction: IAction) {
    const theObjectAction = toAction as IObjectAction;
    const theWaitingTimeAction = toAction as IWaitTimeAction;
    switch (fromAction.sceneObjectType) {
      case SceneObjectType.Light:
      case SceneObjectType.GeneralCommandsLights:
      case SceneObjectType.GroupOfLights:
        theObjectAction.sceneLightState = fromAction.sceneLightState;
        break;
      case SceneObjectType.Dimmer:
        theObjectAction.dimmerStatus = fromAction.dimmerStatus;
        theObjectAction.dimmerPercentage = fromAction.dimmerPercentage;
        break;

      case SceneObjectType.Thermostat:
        theObjectAction.thermostatStatus = fromAction.thermostatStatus;
        theObjectAction.thermostatTriggerTemperature =
          fromAction.thermostatTriggerTemperature;
        break;

      case SceneObjectType.Shutter:
      case SceneObjectType.GeneralCommandsShutters:
      case SceneObjectType.GroupOfShutters:
        theObjectAction.sceneShutterState = fromAction.sceneShutterState;
        break;

      case SceneObjectType.DateTime:
        theWaitingTimeAction.waitTime = String(fromAction.waitTime);
        break;
      case SceneObjectType.Blind:
        theObjectAction.sceneBlindState = fromAction.sceneBlindState;
        break;

      case SceneObjectType.Socket:
        theObjectAction.sceneSocketState = fromAction.sceneSocketState;
        break;

      case SceneObjectType.Lock:
        theObjectAction.sceneLockState = fromAction.sceneLockState;
        break;

      default:
        console.error('Error adding object to a scene: invalid object');
        return;
    }
  }

  handleRemove(action: ActionDTO) {
    this.scene.actions = this.scene.actions?.filter(
      (x) => x.actionParams?.id !== action.actionParams?.id
    );
  }

  openReorderDialog() {
    this.isVisibleDialogReorderAction = true;
  }

  handleDoReorderAction(actions: ActionDTO[]) {
    this.scene.actions = actions;
  }
}
